import { TGeogridProductData, IGeogridProductInfo } from '../data/IUnifiedProductData';

/**
 * Interface for auto-selected product result
 */
export interface IAutoSelectedProduct {
    productName: string;
    tensileStrength: number;
    isAutoSelected: true;
}

/**
 * Finds a specific product within the product data.
 */
export function findProductInfo(productData: TGeogridProductData | null | undefined, productFamily: string | undefined, productName: string | undefined): IGeogridProductInfo | undefined {
    if (!productData || !productFamily || !productName || !productData[productFamily]) {
        return undefined;
    }
    return productData[productFamily].find(item => item.prodotto === productName);
}

/**
 * Gets the tensile strength for a specific design strain percentage,
 * mimicking the VLOOKUP and nested IF logic.
 *
 * @param productData The full product database.
 * @param productFamily The selected product family (e.g., "MacGrid WG").
 * @param productName The specific product name (e.g., "MACGRID WG 03S").
 * @param designStrainPercent The design strain as a number (e.g., 1, 2, 3, 4, 5).
 * @returns The corresponding tensile strength value (e.g., tensile_1_percent) or null if not found/applicable.
 */
export function getTensileAtStrain(
    productData: TGeogridProductData | null | undefined,
    productFamily: string | undefined,
    productName: string | undefined,
    designStrainPercent: number | undefined | null
): number | null {
    const productInfo = findProductInfo(productData, productFamily, productName);

    if (!productInfo || designStrainPercent === undefined || designStrainPercent === null) {
        return null; // Equivalent to VLOOKUP not finding the row or invalid input
    }

    // Determine the property key based on the strain percentage
    let value: number | null = null;

    // Use Math.round to handle potential floating point inaccuracies if strain isn't exactly integer
    const strain = Math.round(designStrainPercent);

    switch (strain) {
        case 1:
            value = productInfo.tensile_1_percent;
            break;
        case 2:
            value = productInfo.tensile_2_percent;
            break;
        case 3:
            value = productInfo.tensile_3_percent;
            break;
        case 4:
            value = productInfo.tensile_4_percent;
            break;
        case 5:
            value = productInfo.tensile_5_percent;
            break;
        default:
            // Excel formula returns FALSE here, which VLOOKUP fails on.
            // We return null to indicate no specific value found for this strain.
            value = null;
            break;
    }

    //console.log(`For ${productName} at ${designStrainPercent}% strain, selected value is: ${value}`);

    // Return the found value (could be null if the data source has null for that percentage)
    return value;
}

/**
 * Finds the optimal product in a family that meets the required tensile strength.
 * Selects the product with the minimum tensile strength that still meets or exceeds
 * the required strength (most economical choice).
 *
 * @param productData The full product database.
 * @param productFamily The selected product family (e.g., "MacGrid WG").
 * @param designStrainPercent The design strain as a number (1, 2, 3, 4, or 5).
 * @param requiredStrength The minimum required tensile strength.
 * @returns The optimal product with tensile strength >= requiredStrength, or null if none found.
 */
export function findOptimalProductForFamily(
    productData: TGeogridProductData | null | undefined,
    productFamily: string | undefined,
    designStrainPercent: number | undefined | null,
    requiredStrength: number | undefined | null
): IAutoSelectedProduct | null {
    // Validate inputs
    if (!productData || !productFamily || !productData[productFamily]) {
        return null;
    }
    if (designStrainPercent == null || requiredStrength == null) {
        return null;
    }

    // Validate strain percentage (must be 1-5)
    const strain = Math.round(designStrainPercent);
    if (![1, 2, 3, 4, 5].includes(strain)) {
        return null;
    }

    // Determine the property key based on strain percentage
    const strainKey = `tensile_${strain}_percent` as keyof IGeogridProductInfo;

    const familyProducts = productData[productFamily];
    if (!familyProducts || familyProducts.length === 0) {
        return null;
    }

    // Filter products with tensile >= required, then find minimum
    const qualifyingProducts = familyProducts
        .map(product => ({
            product,
            tensile: product[strainKey] as number | null,
        }))
        .filter(({ tensile }) => tensile !== null && tensile >= requiredStrength);

    if (qualifyingProducts.length === 0) {
        return null;
    }

    // Find the product with minimum tensile strength (most economical)
    const optimal = qualifyingProducts.reduce((best, current) => (current.tensile! < best.tensile! ? current : best));

    return {
        productName: optimal.product.prodotto,
        tensileStrength: optimal.tensile!,
        isAutoSelected: true,
    };
}
